'use client';

import { useState, useEffect, useRef } from 'react';
import { Category } from '@/types';
import apiClient from '@/lib/api';

interface CategoryAutocompleteProps {
  selectedCategories: Category[];
  onCategoriesChange: (categories: Category[]) => void;
  placeholder?: string;
  className?: string;
}

export default function CategoryAutocomplete({
  selectedCategories,
  onCategoriesChange,
  placeholder = "Search categories...",
  className = ""
}: CategoryAutocompleteProps) {
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState<Category[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch suggestions when query changes
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (query.length < 2) {
        setSuggestions([]);
        return;
      }

      setLoading(true);
      try {
        const results = await apiClient.searchCategories(query);
        // Filter out already selected categories
        const filteredResults = results.filter(
          category => !selectedCategories.some(selected => selected._id === category._id)
        );
        setSuggestions(filteredResults);
      } catch (error) {
        console.error('Error fetching category suggestions:', error);
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(timeoutId);
  }, [query, selectedCategories]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        !inputRef.current?.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);
    setIsOpen(value.length >= 2);
  };

  const handleSelectCategory = (category: Category) => {
    if (!selectedCategories.some(selected => selected._id === category._id)) {
      onCategoriesChange([...selectedCategories, category]);
    }
    setQuery('');
    setIsOpen(false);
  };

  const handleRemoveCategory = (categoryId: string) => {
    onCategoriesChange(selectedCategories.filter(cat => cat._id !== categoryId));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setQuery('');
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Selected Categories */}
      {selectedCategories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {selectedCategories.map((category) => (
            <span
              key={category._id}
              className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800"
            >
              <span className="mr-1">{category.icon || '📄'}</span>
              {category.name}
              <button
                type="button"
                onClick={() => handleRemoveCategory(category._id)}
                className="ml-2 text-blue-600 hover:text-blue-800"
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={() => query.length >= 2 && setIsOpen(true)}
        placeholder={placeholder}
        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
      />

      {/* Dropdown */}
      {isOpen && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {loading ? (
            <div className="px-3 py-2 text-gray-500 text-sm">Loading...</div>
          ) : suggestions.length > 0 ? (
            suggestions.map((category) => (
              <button
                key={category._id}
                type="button"
                onClick={() => handleSelectCategory(category)}
                className="w-full text-left px-3 py-2 hover:bg-gray-100 focus:bg-gray-100 focus:outline-none flex items-center"
              >
                <span className="mr-2">{category.icon || '📄'}</span>
                <div>
                  <div className="font-medium text-gray-900">{category.name}</div>
                  {category.description && (
                    <div className="text-sm text-gray-500">{category.description}</div>
                  )}
                  <div className="text-xs text-gray-400">{category.articleCount} articles</div>
                </div>
              </button>
            ))
          ) : query.length >= 2 ? (
            <div className="px-3 py-2 text-gray-500 text-sm">No categories found</div>
          ) : null}
        </div>
      )}
    </div>
  );
}
